import { Spin, Tooltip, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

import { PanoramaInstance } from '../../config/mapyApi';
import {
    DEFAULT_PANORAMA_TOLERANCE,
    FAST_JUMP_FALLBACK_RATIOS,
    MAX_PANORAMA_TRIES,
    MAX_STEPS_PER_ROUND,
    PANORAMA_STEP_DISTANCES,
    PANORAMA_STEP_MODES,
    PANORAMA_STEP_RADII,
    PANORAMA_TOLERANCE_STEP,
    guessResultMode,
} from '../../constants/game';
import { usePanoramaLoader } from '../../hooks/usePanoramaLoader';
import useSMapResize from '../../hooks/useSMapResize';
import { incrementStepCount } from '../../redux/actions/game';
import { resetLastPanoramaPlace, setLastPanoramaPlace } from '../../redux/actions/pano';
import { PanoramaStepMode } from '../../redux/actions/settings';
import { isApiRateLimited, resetThrottleState, sleep } from '../../util/apiThrottle';
import { debounce } from '../../util/debounce';
import { wasLatLonPanoramaUsedInCurrentGame } from '../../util/game';
import { destinationPoint } from '../../util/geo';
import { panoramaCache } from '../../util/panoramaCache';
import { getNearestMunicipality } from '../../util/reverseGeocode';
import { PanoramaStepModeSwitcher } from '../GameHUD/PanoramaStepModeSwitcher';
import { CustomNavigation } from './CustomNavigation';

const RETRY_DELAY_MS = 500;
const FAST_JUMP_RETRY_DELAY_MS = 200;
const SWAP_DESTROY_DELAY_MS = 80;
const PRELOAD_IDLE_DELAY_MS = 800;
const PRELOAD_YAW_TOLERANCE_RAD = 0.3; // ~17°
// Kolik po sobě jdoucích forward skoků musí hráč udělat, než začneme přednačítat
// (idle preload). Práh 2 znamená: jednorázový skok (typicky "skoč a tipni si")
// už nikdy nepřednačítá → žádné zbytečně stažené (a účtované) dlaždice. Preload
// naskočí až když hráč prokáže souvislou jízdu vpřed, kdy se přednačtené panorama
// reálně spotřebuje (a jeho dlaždice by se stáhly tak jako tak).
const PRELOAD_MIN_CONSECUTIVE_FORWARD = 2;
const FAILURE_TOAST_KEY = 'panorama-jump-failure';

type Side = 'A' | 'B';
type JumpDirection = 'forward' | 'backward';

// ===== Akční tlačítka v pravém horním rohu (sjednocené s HUD designem) =====
//
// Vizuál matchuje GameHUD karty (glassmorphism + RoundIndicator-style colored icon box).
// Barevné akcenty (red/blue/green) zachované přes gradient v 28×28 icon boxu.

const spinAnim = keyframes`
    to { transform: rotate(360deg); }
`;

const ActionGroup = styled.div`
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: none;

    @media (max-width: 600px) {
        top: 10px;
        right: 10px;
        gap: 6px;
    }
`;

const ActionCard = styled.div<{ $disabled?: boolean }>`
    background: rgba(18, 18, 20, 0.92);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
    padding: 8px 14px 8px 8px;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: ${props => (props.$disabled ? 'wait' : 'pointer')};
    pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
    user-select: none;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${props => (props.$disabled ? 0.65 : 1)};

    &:hover {
        background: rgba(28, 28, 32, 0.95);
        border-color: rgba(255, 255, 255, 0.15);
        transform: translateX(-3px);
    }

    @media (max-width: 600px) {
        padding: 5px 10px 5px 5px;
        gap: 8px;
    }
`;

const ActionIconBox = styled.div<{ $accent: string; $glow: string }>`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${props => props.$accent};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 2px 8px ${props => props.$glow};
    flex-shrink: 0;

    @media (max-width: 600px) {
        width: 24px;
        height: 24px;
    }
`;

const ActionLabel = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;

    @media (max-width: 600px) {
        font-size: 11px;
    }
`;

const InlineSpinner = styled.div`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    animation: ${spinAnim} 0.8s linear infinite;
`;

const ACCENT_RED = 'linear-gradient(135deg, #c80707 0%, #a00606 100%)';
const ACCENT_BLUE = 'linear-gradient(135deg, #1890ff 0%, #0e6cc7 100%)';
const ACCENT_GREEN = 'linear-gradient(135deg, #16a34a 0%, #0d7c37 100%)';
const GLOW_RED = 'rgba(200, 7, 7, 0.4)';
const GLOW_BLUE = 'rgba(24, 144, 255, 0.4)';
const GLOW_GREEN = 'rgba(22, 163, 74, 0.4)';

const IconBack = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M9 14L4 9l5-5" />
        <path d="M4 9h10a6 6 0 0 1 6 6v3" />
    </svg>
);

const IconRefresh = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="23 4 23 10 17 10" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </svg>
);

const IconVillage = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 2a8 8 0 0 1 8 8c0 5-8 12-8 12S4 15 4 10a8 8 0 0 1 8-8z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const PanoramaStack = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const PanoramaSlot = styled.div<{ $active?: boolean }>`
    position: absolute;
    inset: 0;
    opacity: ${props => (props.$active ? 1 : 0)};
    z-index: ${props => (props.$active ? 2 : 1)};
    pointer-events: ${props => (props.$active ? 'auto' : 'none')};
    transition: opacity 0.05s linear;
`;

interface PanoramaNewProps {
    originalPanoramaPlace: { latitude: number; longitude: number } | null;
    onSetBestPanoramaPlace: (place: { lat: number; lon: number; date?: string }) => void;
    changePanoramaLoadingState: (loading: boolean) => void;
    isGameStarted: boolean;
    isBattle: boolean;
    noMove?: boolean;
    bestPanoramaPlace: { lat: number; lon: number } | null;
    panoramaLoading: boolean;
    isVisible?: boolean;
}

interface PreloadState {
    side: Side;
    targetLat: number;
    targetLon: number;
    yaw: number;
    mode: PanoramaStepMode;
}

/**
 * Panorama komponenta používající JS Panorama API s nativní + vlastní navigací.
 *
 * Architektura:
 *  - Dual-container (A/B) pro plynulý swap bez bílého záblesku.
 *  - Sjednocený `isSwappingRef` lock pro všechny swap operace (fast jump, refresh,
 *    "Zpět na výchozí", "Jít do obce", recreate na změnu módu) — nemůžou se prokřížit.
 *  - Idle preload: po DRUHÉM po sobě jdoucím forward jumpu se po 800ms idle načte
 *    další panorama do skrytého containeru. Další klik forward je pak instantní
 *    (dlaždice už jsou stažené). Práh 2 (PRELOAD_MIN_CONSECUTIVE_FORWARD) brání
 *    plýtvání: jednorázový skok bez pokračování by jinak přednačetl panorama,
 *    kam hráč nikdy nedojde = zbytečně stažené (a per-tile účtované) dlaždice.
 *  - Strafe (Q/E) — boční skok, často selže (mimo silnice nejsou panoramata),
 *    po vyčerpání retry zobrazí krátký toast.
 */
export const PanoramaNew: React.FC<PanoramaNewProps> = ({
    originalPanoramaPlace,
    onSetBestPanoramaPlace,
    changePanoramaLoadingState,
    isGameStarted,
    isBattle,
    noMove = false,
    bestPanoramaPlace,
    panoramaLoading,
    isVisible = true,
}) => {
    const dispatch = useDispatch();
    const { width, height } = useSMapResize();
    const { loaded: panoramaApiLoaded, createPanorama, checkPanoramaExists } = usePanoramaLoader();

    const currentGame = useSelector((state: any) => state.game.currentGame);
    const currentBattleInfo = useSelector((state: any) => state.battle.currentBattle);
    const panoramaStepMode = useSelector(
        (state: any) => state.settings?.panoramaStepMode ?? PANORAMA_STEP_MODES.DEFAULT,
    ) as PanoramaStepMode;

    const stepCount = currentGame?.stepCount ?? 0;
    const isStepLimitReached = stepCount >= MAX_STEPS_PER_ROUND;
    const isFastMode = panoramaStepMode !== PANORAMA_STEP_MODES.DEFAULT;
    const shouldShowNativeNavigation = !noMove && !isStepLimitReached && !isFastMode;

    // ===== DOM kontejnery (dual-container) =====
    const containerARef = useRef<HTMLDivElement>(null);
    const containerBRef = useRef<HTMLDivElement>(null);
    const panoInstanceARef = useRef<PanoramaInstance | null>(null);
    const panoInstanceBRef = useRef<PanoramaInstance | null>(null);
    const activeSideRef = useRef<Side>('A');
    const [activeSide, setActiveSide] = useState<Side>('A');

    const initialPanoramaPositionRef = useRef<{ lat: number; lon: number } | null>(null);
    const lastPanoramaStateRef = useRef<{
        lat: number;
        lon: number;
        yaw?: number;
        pitch?: number;
        fov?: number;
    } | null>(null);

    // ===== Sjednocený swap lock =====
    const isSwappingRef = useRef(false);
    const [isSwapping, setIsSwapping] = useState(false);

    /**
     * Generation token pro swap operace (swapToPanorama, findBestPanorama).
     * Inkrementuje se na: round change (originalPanoramaPlace), unmount.
     * Po každém awaitu (createPanorama / checkPanoramaExists) zachycujeme
     * aktuální hodnotu před awaitem a po něm porovnáme — pokud se mezitím
     * změnila, ignorujeme výsledek (destroy panorama, abort), abychom nezapsali
     * stale state do unmounted komponenty / nového kola.
     */
    const swapGenerationRef = useRef(0);

    // ===== Idle preload =====
    const preloadRef = useRef<PreloadState | null>(null);
    const preloadGenerationRef = useRef(0);
    const preloadInflightPromiseRef = useRef<Promise<void> | null>(null);
    const preloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Počet po sobě jdoucích forward skoků (arrow/klávesa). Reset na: backward,
    // click-to-move, teleport tlačítka, změnu módu a round change. Gate pro preload.
    const consecutiveForwardJumpsRef = useRef(0);

    // ===== Listenery pro pano-place per side =====
    // Tracujeme konkrétní callback registrovaný na každé instanci, abychom
    // mohli volat removeListener PŘED destroy (Mapy.com docs neslibují, že
    // destroy listenery sám čistí — pozdní eventy by mohly omylem inkrementovat
    // stepCount nebo přepsat lastPanoramaPlace).
    const panoPlaceListenerRefA = useRef<((data: any) => void) | null>(null);
    const panoPlaceListenerRefB = useRef<((data: any) => void) | null>(null);

    // ===== Listenery pro pano-view per side (pitch tracking) =====
    // pano-view fires při změně kamery (pan myší). Updatujeme CSS variable
    // `--pano-pitch` na panorama-stack rootu, čímž se vlastní šipky CustomNavigation
    // posouvají vertikálně podle náklonu — simuluje perspektivu nativních arrows.
    const panoViewListenerRefA = useRef<((data: any) => void) | null>(null);
    const panoViewListenerRefB = useRef<((data: any) => void) | null>(null);

    const panoramaStackRef = useRef<HTMLDivElement>(null);

    // ===== Sledování shouldShowNativeNavigation =====
    const prevShouldShowNavRef = useRef(shouldShowNativeNavigation);

    // ===== Reaktivní refs pro klávesnice/preload (closures) =====
    const isFastModeRef = useRef(isFastMode);
    const isStepLimitReachedRef = useRef(isStepLimitReached);
    const noMoveRef = useRef(noMove);
    const panoramaStepModeRef = useRef(panoramaStepMode);

    useEffect(() => {
        isFastModeRef.current = isFastMode;
    }, [isFastMode]);
    useEffect(() => {
        isStepLimitReachedRef.current = isStepLimitReached;
    }, [isStepLimitReached]);
    useEffect(() => {
        noMoveRef.current = noMove;
    }, [noMove]);
    useEffect(() => {
        panoramaStepModeRef.current = panoramaStepMode;
    }, [panoramaStepMode]);

    const [findPanoramaTriesCounter, setFindPanoramaTriesCounter] = useState(0);
    const [panoramaFounded, setPanoramaFounded] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [isFindingMunicipality, setIsFindingMunicipality] = useState(false);

    const wasVisibleRef = useRef(isVisible);

    const { guessResultMode: guessResultModeSinglePlayer, rounds } = currentGame;
    const { guessResultMode: guessResultModeBattle } = currentBattleInfo;

    // ===== Helpers pro dual-container =====

    const getInactiveSide = useCallback((): Side => (activeSideRef.current === 'A' ? 'B' : 'A'), []);
    const getContainerForSide = useCallback(
        (side: Side) => (side === 'A' ? containerARef.current : containerBRef.current),
        [],
    );
    const getInstanceRefForSide = useCallback((side: Side) => (side === 'A' ? panoInstanceARef : panoInstanceBRef), []);

    const getListenerRefForSide = useCallback(
        (side: Side) => (side === 'A' ? panoPlaceListenerRefA : panoPlaceListenerRefB),
        [],
    );

    const getViewListenerRefForSide = useCallback(
        (side: Side) => (side === 'A' ? panoViewListenerRefA : panoViewListenerRefB),
        [],
    );

    const destroyInstanceOnSide = useCallback(
        (side: Side) => {
            const ref = getInstanceRefForSide(side);
            const listenerRef = getListenerRefForSide(side);
            const viewListenerRef = getViewListenerRefForSide(side);
            if (ref.current) {
                // Nejdřív removeListener — pak destroy. Tím zaručíme, že pozdní
                // pano-place / pano-view eventy z destroyed instance neproniknou
                // do Reduxu / DOM.
                if (listenerRef.current) {
                    try {
                        ref.current.removeListener('pano-place', listenerRef.current);
                    } catch {
                        /* ignore */
                    }
                    listenerRef.current = null;
                }
                if (viewListenerRef.current) {
                    try {
                        ref.current.removeListener('pano-view', viewListenerRef.current);
                    } catch {
                        /* ignore */
                    }
                    viewListenerRef.current = null;
                }
                try {
                    ref.current.destroy();
                } catch {
                    /* ignore */
                }
                ref.current = null;
            } else {
                listenerRef.current = null;
                viewListenerRef.current = null;
            }
        },
        [getInstanceRefForSide, getListenerRefForSide, getViewListenerRefForSide],
    );

    // ===== Preload helpers =====

    const clearPreloadTimer = useCallback(() => {
        if (preloadTimerRef.current) {
            clearTimeout(preloadTimerRef.current);
            preloadTimerRef.current = null;
        }
    }, []);

    /**
     * Zruší a destrukne aktuální preload (pokud nějaký je).
     * Pokud je preload právě v inflightu, počká na jeho rezoluci (která detekuje
     * stale generaci a sama si po sobě uklidí). Po awaitu je inactive container
     * garantovaně volný.
     */
    const discardPreload = useCallback(async () => {
        preloadGenerationRef.current++;
        clearPreloadTimer();

        if (preloadInflightPromiseRef.current) {
            try {
                await preloadInflightPromiseRef.current;
            } catch {
                /* ignore */
            }
            preloadInflightPromiseRef.current = null;
        }

        if (preloadRef.current) {
            const { side } = preloadRef.current;
            preloadRef.current = null;
            destroyInstanceOnSide(side);
        }
    }, [clearPreloadTimer, destroyInstanceOnSide]);

    /**
     * Spustí preload příštího "forward" panoramatu do inactive containeru.
     * Volat jen když isSwappingRef.current === false a preload neexistuje.
     */
    const triggerForwardPreload = useCallback(async () => {
        if (!isFastModeRef.current) return;
        if (isStepLimitReachedRef.current || noMoveRef.current) return;
        if (isSwappingRef.current) return;
        if (preloadRef.current) return;
        if (preloadInflightPromiseRef.current) return;

        const last = lastPanoramaStateRef.current;
        if (!last) return;

        const activeInstance = getInstanceRefForSide(activeSideRef.current).current;
        if (!activeInstance) return;

        const camera = activeInstance.getCamera?.();
        const yaw = camera?.yaw ?? last.yaw ?? 0;
        const currentMode = panoramaStepModeRef.current;
        const distance = PANORAMA_STEP_DISTANCES[currentMode];
        if (!distance) return;

        const target = destinationPoint(last.lat, last.lon, distance, yaw);
        const radius = PANORAMA_STEP_RADII[currentMode] ?? 50;

        const inactiveSide = getInactiveSide();
        const container = getContainerForSide(inactiveSide);
        if (!container) return;

        // Inactive container je z definice prázdný (po posledním swapu),
        // ale pro jistotu uklidíme případnou starou instanci.
        destroyInstanceOnSide(inactiveSide);

        const generation = ++preloadGenerationRef.current;

        const promise = (async () => {
            try {
                const pano = await createPanorama(container, target.lon, target.lat, {
                    showNavigation: false,
                    radius,
                    hideErrors: true,
                    yaw,
                });

                if (generation !== preloadGenerationRef.current) {
                    if (pano)
                        try {
                            pano.destroy();
                        } catch {
                            /* ignore */
                        }
                    return;
                }

                if (pano && pano.errorCode === 'NONE') {
                    getInstanceRefForSide(inactiveSide).current = pano;
                    // Použij skutečnou pozici z pano.info (může být snapnutá na nejbližší
                    // existující panorama v rámci radius), fallback na requested target.
                    preloadRef.current = {
                        side: inactiveSide,
                        targetLat: pano.info?.lat ?? target.lat,
                        targetLon: pano.info?.lon ?? target.lon,
                        yaw,
                        mode: currentMode,
                    };
                } else if (pano) {
                    try {
                        pano.destroy();
                    } catch {
                        /* ignore */
                    }
                }
            } catch {
                /* ignore */
            }
        })();

        preloadInflightPromiseRef.current = promise;
        await promise;
        if (preloadInflightPromiseRef.current === promise) {
            preloadInflightPromiseRef.current = null;
        }
    }, [createPanorama, getInactiveSide, getContainerForSide, getInstanceRefForSide, destroyInstanceOnSide]);

    /**
     * Naplánuje preload `PRELOAD_IDLE_DELAY_MS` ms po posledním jumpu.
     * Předchozí timer zruší.
     */
    const schedulePreload = useCallback(() => {
        clearPreloadTimer();
        if (!isFastModeRef.current) return;
        if (isStepLimitReachedRef.current || noMoveRef.current) return;
        // Přednačítáme až po prokázané souvislé jízdě vpřed — viz
        // PRELOAD_MIN_CONSECUTIVE_FORWARD. Šetří dlaždice u jednorázových skoků.
        if (consecutiveForwardJumpsRef.current < PRELOAD_MIN_CONSECUTIVE_FORWARD) return;

        preloadTimerRef.current = setTimeout(() => {
            triggerForwardPreload();
        }, PRELOAD_IDLE_DELAY_MS);
    }, [clearPreloadTimer, triggerForwardPreload]);

    /**
     * Když uživatel klikne forward a preload existuje + je platný (stejný mód, podobný yaw),
     * udělá instantní swap bez API volání. Vrací true při úspěšném consume.
     */
    const tryConsumeForwardPreload = useCallback((): boolean => {
        const preload = preloadRef.current;
        if (!preload) return false;
        if (preload.mode !== panoramaStepModeRef.current) return false;

        const activeInstance = getInstanceRefForSide(activeSideRef.current).current;
        const currentCamera = activeInstance?.getCamera?.();
        const currentYaw = currentCamera?.yaw ?? 0;

        // Yaw drift check pomocí atan2(sin, cos) — robustní cyklická vzdálenost,
        // funguje správně i kdyby getCamera() vrátil yaw mimo [0, 2π] (po dlouhých
        // sessions může drift kumulovat).
        const yawDiff = Math.abs(Math.atan2(Math.sin(currentYaw - preload.yaw), Math.cos(currentYaw - preload.yaw)));
        if (yawDiff > PRELOAD_YAW_TOLERANCE_RAD) return false;

        // Consume
        const oldSide = activeSideRef.current;
        const newSide = preload.side;
        const newInstance = getInstanceRefForSide(newSide).current;

        // Aplikuj aktuální kameru na nové panorama (preserve view continuity)
        if (newInstance && currentCamera) {
            try {
                newInstance.setCamera({
                    yaw: currentYaw,
                    pitch: currentCamera.pitch ?? 0,
                    fov: currentCamera.fov ?? 1.2,
                });
            } catch {
                /* ignore */
            }
        }

        // Token-based destroy: pokud mezi consumem a 80ms timeoutem proběhne
        // jiný swap, který znovu použije oldSide kontejner (jako inactive),
        // jeho fresh instance NESMÍ být zničena tímto starým timerem.
        const oldInstanceRef = getInstanceRefForSide(oldSide);
        const oldListenerRef = getListenerRefForSide(oldSide);
        const oldViewListenerRef = getViewListenerRefForSide(oldSide);
        const instanceToDestroy = oldInstanceRef.current;
        const listenerToRemove = oldListenerRef.current;
        const viewListenerToRemove = oldViewListenerRef.current;

        activeSideRef.current = newSide;
        setActiveSide(newSide);

        lastPanoramaStateRef.current = {
            lat: preload.targetLat,
            lon: preload.targetLon,
            yaw: currentYaw,
            pitch: currentCamera?.pitch,
            fov: currentCamera?.fov,
        };

        preloadRef.current = null;

        setTimeout(() => {
            if (oldInstanceRef.current === instanceToDestroy && instanceToDestroy) {
                // Sjednocený invariant: removeListener PŘED destroy.
                if (listenerToRemove) {
                    try {
                        instanceToDestroy.removeListener('pano-place', listenerToRemove);
                    } catch {
                        /* ignore */
                    }
                    if (oldListenerRef.current === listenerToRemove) {
                        oldListenerRef.current = null;
                    }
                }
                if (viewListenerToRemove) {
                    try {
                        instanceToDestroy.removeListener('pano-view', viewListenerToRemove);
                    } catch {
                        /* ignore */
                    }
                    if (oldViewListenerRef.current === viewListenerToRemove) {
                        oldViewListenerRef.current = null;
                    }
                }
                try {
                    instanceToDestroy.destroy();
                } catch {
                    /* ignore */
                }
                oldInstanceRef.current = null;
            }
        }, SWAP_DESTROY_DELAY_MS);

        dispatch(setLastPanoramaPlace({ lat: preload.targetLat, lon: preload.targetLon }));
        dispatch(incrementStepCount());

        return true;
    }, [dispatch, getInstanceRefForSide, getListenerRefForSide, getViewListenerRefForSide]);

    // ===== Position tracking (debounced Redux update) =====

    const debouncedPositionHandler = useMemo(
        () =>
            debounce((lat: number, lon: number) => {
                const activeInstance = getInstanceRefForSide(activeSideRef.current).current;
                const camera = activeInstance?.getCamera?.();
                lastPanoramaStateRef.current = {
                    lat,
                    lon,
                    yaw: camera?.yaw,
                    pitch: camera?.pitch,
                    fov: camera?.fov,
                };
                dispatch(setLastPanoramaPlace({ lat, lon }));
            }, 500),
        [dispatch, getInstanceRefForSide],
    );

    const handlePanoramaChange = useCallback(
        (lat: number, lon: number) => {
            dispatch(incrementStepCount());
            debouncedPositionHandler(lat, lon);
        },
        [dispatch, debouncedPositionHandler],
    );

    // ===== "Zpět na výchozí" tlačítko =====

    const showGoBackToTheBeginning = useMemo(() => {
        const activeInstance = getInstanceRefForSide(activeSide).current;
        const isPanoramaLoaded = activeInstance && bestPanoramaPlace;

        if (isBattle) {
            if (guessResultModeBattle === guessResultMode.end && isPanoramaLoaded) return true;
        } else if (guessResultModeSinglePlayer === guessResultMode.end && isPanoramaLoaded) {
            return true;
        }
        return false;
    }, [
        isBattle,
        guessResultModeSinglePlayer,
        guessResultModeBattle,
        bestPanoramaPlace,
        activeSide,
        getInstanceRefForSide,
    ]);

    // ===== Hlavní swap funkce =====

    const swapToPanorama = useCallback(
        async (
            lon: number,
            lat: number,
            camera?: { yaw?: number; pitch?: number; fov?: number },
            options: { showNavigation?: boolean; isInitialLoad?: boolean; radius?: number } = {},
        ): Promise<{ success: boolean; errorCode?: string; actualLat?: number; actualLon?: number }> => {
            const { showNavigation = false, isInitialLoad = false, radius = 50 } = options;

            if (!panoramaApiLoaded) {
                return { success: false, errorCode: 'API_NOT_LOADED' };
            }

            const targetSide: Side = isInitialLoad ? activeSideRef.current : getInactiveSide();
            const targetContainer = getContainerForSide(targetSide);
            const targetInstanceRef = getInstanceRefForSide(targetSide);
            const targetListenerRef = getListenerRefForSide(targetSide);

            if (!targetContainer) {
                return { success: false, errorCode: 'NO_CONTAINER' };
            }

            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            if (targetInstanceRef.current) {
                if (targetListenerRef.current) {
                    try {
                        targetInstanceRef.current.removeListener('pano-place', targetListenerRef.current);
                    } catch {
                        /* ignore */
                    }
                    targetListenerRef.current = null;
                }
                try {
                    targetInstanceRef.current.destroy();
                } catch {
                    /* ignore */
                }
                targetInstanceRef.current = null;
            }

            // Generation token capture PŘED awaitem — pokud se mezitím změní
            // kolo nebo komponenta unmountne, výsledek zahodíme.
            const generation = swapGenerationRef.current;

            const pano = await createPanorama(targetContainer, lon, lat, {
                showNavigation,
                radius,
                hideErrors: true,
                yaw: camera?.yaw,
                pitch: camera?.pitch,
                fov: camera?.fov,
            });

            // Stale check — round/unmount mohl proběhnout během awaitu.
            if (generation !== swapGenerationRef.current) {
                if (pano) {
                    try {
                        pano.destroy();
                    } catch {
                        /* ignore */
                    }
                }
                return { success: false, errorCode: 'STALE' };
            }

            if (!pano || pano.errorCode !== 'NONE') {
                if (pano) {
                    try {
                        pano.destroy();
                    } catch {
                        /* ignore */
                    }
                }
                return { success: false, errorCode: pano?.errorCode };
            }

            // Skutečná pozice = pano.info (může být snapnutá na nejbližší existující
            // panorama v rámci radius). Fallback na requested lat/lon.
            const actualLat = pano.info?.lat ?? lat;
            const actualLon = pano.info?.lon ?? lon;

            targetInstanceRef.current = pano;

            if (!initialPanoramaPositionRef.current) {
                initialPanoramaPositionRef.current = { lat: actualLat, lon: actualLon };
            }
            lastPanoramaStateRef.current = {
                lat: actualLat,
                lon: actualLon,
                yaw: camera?.yaw,
                pitch: camera?.pitch,
                fov: camera?.fov,
            };

            if (showNavigation) {
                const callback = (data: any) => {
                    if (data?.info) {
                        handlePanoramaChange(data.info.lat, data.info.lon);
                    }
                };
                pano.addListener('pano-place', callback);
                targetListenerRef.current = callback;
            }

            // pano-view listener pro pitch tracking (CustomNavigation perspektiva).
            // Registruje se vždy — i v DEFAULT módu je no-op (CSS variable se updatuje,
            // ale CustomNavigation není rendrovaná). Cena: jeden listener navíc.
            const targetViewListenerRef = getViewListenerRefForSide(targetSide);
            const viewCallback = () => {
                const cam = pano.getCamera?.();
                if (cam && panoramaStackRef.current) {
                    panoramaStackRef.current.style.setProperty('--pano-pitch', String(cam.pitch ?? 0));
                }
            };
            try {
                pano.addListener('pano-view', viewCallback);
                targetViewListenerRef.current = viewCallback;
                // Zavoláme jednou ručně, ať se variable nastaví hned po loadu (před prvním pohybem).
                viewCallback();
            } catch {
                /* ignore — pokud Mapy.com nepodporuje, zůstaneme bez perspektivy */
            }

            if (!isInitialLoad) {
                const oldSide = activeSideRef.current;
                const oldInstanceRef = getInstanceRefForSide(oldSide);
                const oldListenerRef = getListenerRefForSide(oldSide);
                const oldViewListenerRef = getViewListenerRefForSide(oldSide);
                // Token-based destroy: zachytíme konkrétní instanci, kterou chceme zničit.
                // Pokud mezitím někdo do oldSide kontejneru nahraje nové panorama
                // (např. další swap, který nás použije jako inactive), nesmíme zničit JEHO panorama.
                const instanceToDestroy = oldInstanceRef.current;
                const listenerToRemove = oldListenerRef.current;
                const viewListenerToRemove = oldViewListenerRef.current;

                activeSideRef.current = targetSide;
                setActiveSide(targetSide);

                setTimeout(() => {
                    if (oldInstanceRef.current === instanceToDestroy && instanceToDestroy) {
                        if (listenerToRemove) {
                            try {
                                instanceToDestroy.removeListener('pano-place', listenerToRemove);
                            } catch {
                                /* ignore */
                            }
                            if (oldListenerRef.current === listenerToRemove) {
                                oldListenerRef.current = null;
                            }
                        }
                        if (viewListenerToRemove) {
                            try {
                                instanceToDestroy.removeListener('pano-view', viewListenerToRemove);
                            } catch {
                                /* ignore */
                            }
                            if (oldViewListenerRef.current === viewListenerToRemove) {
                                oldViewListenerRef.current = null;
                            }
                        }
                        try {
                            instanceToDestroy.destroy();
                        } catch {
                            /* ignore */
                        }
                        oldInstanceRef.current = null;
                    }
                }, SWAP_DESTROY_DELAY_MS);
            }

            return { success: true, actualLat, actualLon };
        },
        [
            panoramaApiLoaded,
            createPanorama,
            getInactiveSide,
            getContainerForSide,
            getInstanceRefForSide,
            getListenerRefForSide,
            getViewListenerRefForSide,
            handlePanoramaChange,
        ],
    );

    /**
     * Wrapper akquiruje sdílený lock + spinner state. Použít všude, kde voláme
     * swapToPanorama z user-iniciovaných handlerů (jump, refresh, "Zpět", "Jít do obce").
     */
    const withSwapLock = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
        if (isSwappingRef.current) return null;
        isSwappingRef.current = true;
        setIsSwapping(true);
        try {
            return await fn();
        } finally {
            isSwappingRef.current = false;
            setIsSwapping(false);
        }
    }, []);

    const showPanoramaAt = useCallback(
        (
            lon: number,
            lat: number,
            camera?: { yaw?: number; pitch?: number; fov?: number },
            forceDisableNavigation = false,
            isInitialLoad = false,
        ) => {
            const showNavigation = !forceDisableNavigation && shouldShowNativeNavigation;
            return swapToPanorama(lon, lat, camera, { showNavigation, isInitialLoad });
        },
        [shouldShowNativeNavigation, swapToPanorama],
    );

    // ===== Hledání panoramatu (start kola) =====

    const findBestPanorama = useCallback(async () => {
        if (!originalPanoramaPlace || !panoramaApiLoaded) return;

        // Generation token: zachytíme NA ZAČÁTKU. Po každém await kontrolujeme,
        // zda se mezi tím nezměnilo kolo. Pokud ano, abort bez zápisu state —
        // jinak by starý find po round change nahrál staré panorama do nového kola.
        const generation = swapGenerationRef.current;
        const isStale = () => generation !== swapGenerationRef.current;

        const { latitude, longitude } = originalPanoramaPlace;

        let tolerance = DEFAULT_PANORAMA_TOLERANCE;
        if (findPanoramaTriesCounter > 0) {
            tolerance = findPanoramaTriesCounter * PANORAMA_TOLERANCE_STEP;
            if (findPanoramaTriesCounter === MAX_PANORAMA_TRIES) tolerance = 5000;
        }

        setIsSearching(true);

        try {
            const cachedPlace = panoramaCache.get(latitude, longitude, tolerance);
            if (cachedPlace) {
                const { lat, lon } = cachedPlace;
                const alreadyUsed =
                    wasLatLonPanoramaUsedInCurrentGame(lat, lon, rounds) || panoramaCache.wasUsedInSession(lat, lon);

                if (!alreadyUsed) {
                    const result = await swapToPanorama(lon, lat, undefined, {
                        showNavigation: shouldShowNativeNavigation,
                        isInitialLoad: true,
                    });
                    if (isStale()) return;
                    if (result.success) {
                        panoramaCache.markAsUsed(lat, lon);
                        onSetBestPanoramaPlace({ lat, lon });
                        changePanoramaLoadingState(false);
                        setFindPanoramaTriesCounter(0);
                    }
                    return;
                }
            }

            const existsResult = await checkPanoramaExists(longitude, latitude, tolerance);
            if (isStale()) return;

            if (existsResult?.exists && existsResult.info) {
                const { lat, lon } = existsResult.info;
                panoramaCache.set(latitude, longitude, tolerance, { lat, lon, date: existsResult.info.date });

                const alreadyFoundPanorama =
                    wasLatLonPanoramaUsedInCurrentGame(lat, lon, rounds) || panoramaCache.wasUsedInSession(lat, lon);

                if (alreadyFoundPanorama) {
                    if (findPanoramaTriesCounter < MAX_PANORAMA_TRIES) {
                        await sleep(RETRY_DELAY_MS);
                        if (isStale()) return;
                        setFindPanoramaTriesCounter(prev => prev + 1);
                    } else {
                        changePanoramaLoadingState(false);
                        setPanoramaFounded(false);
                    }
                } else {
                    const result = await swapToPanorama(lon, lat, undefined, {
                        showNavigation: shouldShowNativeNavigation,
                        isInitialLoad: true,
                    });
                    if (isStale()) return;
                    if (result.success) {
                        panoramaCache.markAsUsed(lat, lon);
                        onSetBestPanoramaPlace({ lat, lon, date: existsResult.info.date });
                        changePanoramaLoadingState(false);
                        setFindPanoramaTriesCounter(0);
                    }
                }
            } else if (findPanoramaTriesCounter < MAX_PANORAMA_TRIES) {
                await sleep(RETRY_DELAY_MS);
                if (isStale()) return;
                setFindPanoramaTriesCounter(prev => prev + 1);
            } else {
                changePanoramaLoadingState(false);
                setPanoramaFounded(false);
            }
        } catch (err) {
            if (isStale()) return;
            console.error('Error finding panorama:', err);
            if (findPanoramaTriesCounter < MAX_PANORAMA_TRIES) {
                const delayMs = isApiRateLimited() ? RETRY_DELAY_MS * 4 : RETRY_DELAY_MS * 2;
                await sleep(delayMs);
                if (isStale()) return;
                setFindPanoramaTriesCounter(prev => prev + 1);
            } else {
                changePanoramaLoadingState(false);
                setPanoramaFounded(false);
            }
        } finally {
            // Při stale clearu nechceme přepsat state nového kola; nové kolo
            // si svůj isSearching řídí samo (round-change effect ho resetuje).
            if (!isStale()) setIsSearching(false);
        }
    }, [
        originalPanoramaPlace,
        panoramaApiLoaded,
        findPanoramaTriesCounter,
        rounds,
        changePanoramaLoadingState,
        onSetBestPanoramaPlace,
        swapToPanorama,
        shouldShowNativeNavigation,
        checkPanoramaExists,
    ]);

    // ===== Fast jump (forward / backward / left / right) =====

    /**
     * Skok ve směru `direction` (forward = yaw, backward = yaw + π).
     * Volitelný `bearingOverride` přebíjí výpočet — používá se pro click-to-move
     * (uživatel klikne na panorama a my vypočítáme bearing z polohy kliku).
     *
     *  Forward využívá idle preload (instantní swap, 0 API calls), pokud je preload
     *  platný. Backward i bearingOverride vždy provádějí regular swap.
     *
     *  Selhaný jump (po fallback retry) → toast, žádný incrementStepCount.
     */
    const handleFastJump = useCallback(
        async (direction: JumpDirection, bearingOverride?: number) => {
            if (isStepLimitReachedRef.current) return;
            if (noMoveRef.current) return;
            if (!isFastModeRef.current) return;

            const isClickJump = bearingOverride !== undefined;

            // Backward i click-to-move přerušují souvislou jízdu vpřed → reset čítače.
            // Další preload se pak spustí až po novém "rozjezdu" vpřed.
            if (direction === 'backward' || isClickJump) {
                consecutiveForwardJumpsRef.current = 0;
            }

            // Forward (z arrow tlačítka / klávesy) — zkusíme consume preload.
            // Click jump preload neumí konzumovat (preload byl pro yaw, click může být jiný směr).
            if (direction === 'forward' && !isClickJump && !isSwappingRef.current) {
                clearPreloadTimer();
                if (tryConsumeForwardPreload()) {
                    consecutiveForwardJumpsRef.current += 1;
                    schedulePreload(); // chain
                    return;
                }
            }

            // Pokud je preload v inflightu nebo existuje hotový, zahodíme (čeká
            // na inflight → uvolní inactive container).
            await discardPreload();

            // Lock proti dvojkliku / kombinaci s jinými swap akcemi.
            // Návratová hodnota: true = jump proběhl a uspěl, false = proběhl a selhal,
            // null = lock byl obsazený (jump zahozen). Preload plánujeme jen při true.
            const swapRan = await withSwapLock(async () => {
                const currentMode = panoramaStepModeRef.current;
                const distanceConst = PANORAMA_STEP_DISTANCES[currentMode];
                if (!distanceConst) return;

                const activeInstance = getInstanceRefForSide(activeSideRef.current).current;
                const last = lastPanoramaStateRef.current;
                if (!activeInstance || !last) return;

                const camera = activeInstance.getCamera?.();
                const yaw = camera?.yaw ?? last.yaw ?? 0;
                const pitch = camera?.pitch ?? last.pitch;
                const fov = camera?.fov ?? last.fov;

                let bearing: number;
                if (bearingOverride !== undefined) {
                    bearing = bearingOverride;
                } else if (direction === 'backward') {
                    bearing = yaw + Math.PI;
                } else {
                    bearing = yaw;
                }

                let succeeded = false;

                // Per-mode snap radius — proporční ~50 % distance, drží absolutní
                // chybu úměrnou cílové vzdálenosti (STEP_1 ±25 m, STEP_3 ±100 m).
                const radius = PANORAMA_STEP_RADII[currentMode] ?? 50;

                // Sequential retry s postupně zmenšující se vzdáleností — záměrný
                // sequential pattern, ne paralelní (každý další pokus závisí na výsledku předchozího).
                /* eslint-disable no-await-in-loop, no-restricted-syntax, no-continue */
                for (const ratio of FAST_JUMP_FALLBACK_RATIOS) {
                    const distance = distanceConst * ratio;
                    const target = destinationPoint(last.lat, last.lon, distance, bearing);

                    const result = await swapToPanorama(
                        target.lon,
                        target.lat,
                        { yaw, pitch, fov },
                        {
                            showNavigation: false,
                            isInitialLoad: false,
                            radius,
                        },
                    );

                    if (result.success) {
                        const finalState = lastPanoramaStateRef.current;
                        if (finalState) {
                            dispatch(setLastPanoramaPlace({ lat: finalState.lat, lon: finalState.lon }));
                        }
                        dispatch(incrementStepCount());
                        if (direction === 'forward' && !isClickJump) {
                            consecutiveForwardJumpsRef.current += 1;
                        }
                        succeeded = true;
                        break;
                    }

                    if (result.errorCode === 'PANORAMA_NOT_FOUND') {
                        await sleep(FAST_JUMP_RETRY_DELAY_MS);
                        continue;
                    }
                    break; // jiná chyba → konec
                }
                /* eslint-enable no-await-in-loop, no-restricted-syntax, no-continue */

                if (!succeeded) {
                    message.warning({
                        key: FAILURE_TOAST_KEY,
                        content: 'Tímto směrem už silnice nepokračuje.',
                        duration: 1.8,
                    });
                }

                return succeeded;
            });

            // Po dokončení: forward arrow/klávesa → naplánuj další preload, ALE jen
            // když jump opravdu proběhl a uspěl (swapRan === true). Když byl zahozen
            // lockem (null = obsazeno, no-op) nebo selhal (false = slepá ulička),
            // nepreloadujeme — jinak bychom stáhli dlaždice pro panorama, kam se
            // hráč buď nedostane, nebo tam vůbec není silnice. Click jump nepreloaduje.
            if (direction === 'forward' && !isClickJump && swapRan === true) {
                schedulePreload();
            }
        },
        [
            clearPreloadTimer,
            tryConsumeForwardPreload,
            schedulePreload,
            discardPreload,
            withSwapLock,
            getInstanceRefForSide,
            swapToPanorama,
            dispatch,
        ],
    );

    // ===== Klávesnice (jen ve fast módu) =====

    useEffect(() => {
        if (!isFastMode || noMove || isStepLimitReached) return;

        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target) {
                const tag = target.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
            }
            // Ignoruj kombinace s modifikátory — necháme nativní zkratky
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            const k = e.key;
            if (k === 'ArrowUp' || k === 'w' || k === 'W') {
                e.preventDefault();
                handleFastJump('forward');
            } else if (k === 'ArrowDown' || k === 's' || k === 'S') {
                e.preventDefault();
                handleFastJump('backward');
            }
            // ArrowLeft/Right + Q/E ponecháme nativní (rotace kamery / nic)
        };

        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isFastMode, noMove, isStepLimitReached, handleFastJump]);

    // ===== Click-to-move (jen ve fast módu) =====
    //
    // V nativní DEFAULT navigaci umí Mapy.com sama: klik na cestu = posun.
    // Ve fast módu je showNavigation=false, takže click event nezachytávají.
    // Přidáme tedy vlastní pointer handler na panorama containery, který:
    //  - Pomocí pointerdown + pointerup detekuje krátký klik (žádný drag).
    //  - Drag (>8 px posun) ignoruje — Mapy.com si ho nechá pro rotaci kamery.
    //  - Z polohy kliku spočítá horizontální úhel od středu viewportu (přes FOV),
    //    přidá k aktuálnímu yaw a fast-jumpe v té směrovce o aktuální step distance.

    useEffect(() => {
        if (!isFastMode || noMove || isStepLimitReached) return;

        const containerA = containerARef.current;
        const containerB = containerBRef.current;
        if (!containerA || !containerB) return;

        // Velkorysý threshold pro touch — uživatel na mobilu obvykle "tapne"
        // s mírnou nepřesností (1–8 px). Drag = > 10 px posun KDYKOLIV během gesta.
        const DRAG_THRESHOLD_PX = 10;
        const CLICK_MAX_DURATION_MS = 500;

        interface PointerStart {
            x: number;
            y: number;
            t: number;
            el: HTMLElement;
            maxDistance: number;
        }
        const downByPointer = new Map<number, PointerStart>();

        const handleDown = (e: PointerEvent) => {
            downByPointer.set(e.pointerId, {
                x: e.clientX,
                y: e.clientY,
                t: Date.now(),
                el: e.currentTarget as HTMLElement,
                maxDistance: 0,
            });
        };

        const handleMove = (e: PointerEvent) => {
            const start = downByPointer.get(e.pointerId);
            if (!start) return;
            const dx = e.clientX - start.x;
            const dy = e.clientY - start.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > start.maxDistance) start.maxDistance = dist;
        };

        const handleUp = (e: PointerEvent) => {
            const start = downByPointer.get(e.pointerId);
            downByPointer.delete(e.pointerId);
            if (!start) return;

            // Drag detection: maximální posun PĚHEM gesta, ne jen endpoint —
            // jinak by drag-in-kruh (uživatel drží a vrátí kurzor zpátky)
            // chybně triggeroval click jump.
            if (start.maxDistance > DRAG_THRESHOLD_PX) return;
            if (Date.now() - start.t > CLICK_MAX_DURATION_MS) return;

            // Z polohy kliku v rámci containeru spočítáme horizontální úhel
            // od středu viewportu, používá se kamera FOV pro perspektivní projekci.
            const rect = start.el.getBoundingClientRect();
            if (rect.width <= 0) return;
            const offsetX = e.clientX - rect.left - rect.width / 2;

            const activeInstance = getInstanceRefForSide(activeSideRef.current).current;
            const camera = activeInstance?.getCamera?.();
            if (!camera) return;

            const yaw = camera.yaw ?? 0;
            const fov = camera.fov ?? 1.2;

            // Pinhole model: úhel = atan2(offsetPx, focalLengthPx),
            // kde focalLength = (width / 2) / tan(fov / 2).
            const focalLength = rect.width / 2 / Math.tan(fov / 2);
            const horizontalAngle = Math.atan2(offsetX, focalLength);
            const targetBearing = yaw + horizontalAngle;

            handleFastJump('forward', targetBearing);
        };

        const handleCancel = (e: PointerEvent) => {
            downByPointer.delete(e.pointerId);
        };

        const targets = [containerA, containerB];
        targets.forEach(t => {
            t.addEventListener('pointerdown', handleDown);
            t.addEventListener('pointermove', handleMove);
            t.addEventListener('pointerup', handleUp);
            t.addEventListener('pointercancel', handleCancel);
        });

        return () => {
            targets.forEach(t => {
                t.removeEventListener('pointerdown', handleDown);
                t.removeEventListener('pointermove', handleMove);
                t.removeEventListener('pointerup', handleUp);
                t.removeEventListener('pointercancel', handleCancel);
            });
        };
    }, [isFastMode, noMove, isStepLimitReached, handleFastJump, getInstanceRefForSide]);

    // ===== Hlavní effect pro hledání panoramatu (start kola) =====

    useEffect(() => {
        if (panoramaApiLoaded && originalPanoramaPlace && isGameStarted && !bestPanoramaPlace && !isSearching) {
            findBestPanorama();
        }
    }, [
        panoramaApiLoaded,
        originalPanoramaPlace,
        isGameStarted,
        bestPanoramaPlace,
        isSearching,
        findPanoramaTriesCounter,
        findBestPanorama,
    ]);

    // ===== Round change → zahoď preload + uklid inactive side =====

    useEffect(() => {
        // Round-change reset spouštíme jen když bestPanoramaPlace je null,
        // tj. mezi koly / před prvním findem. Parent (GameScreen.handleSetBestPanoramaPlace)
        // PO úspěšném findu updatuje originalPanoramaPlace na actual lat/lon
        // (synchronizace s pano.info), ale to NENÍ round change — bestPanoramaPlace je truthy.
        // Bez tohoto guardu by se po každém úspěšném findu vyresetoval lastPanoramaStateRef
        // a fast jump by bailoval na `if (!last) return;`.
        if (bestPanoramaPlace) return;

        // Skutečné round change: nové kolo nebo refresh, kdy parent setBestPanoramaPlace(null).
        swapGenerationRef.current++;
        discardPreload();
        consecutiveForwardJumpsRef.current = 0;
        const inactive = activeSideRef.current === 'A' ? 'B' : 'A';
        destroyInstanceOnSide(inactive);
        initialPanoramaPositionRef.current = null;
        lastPanoramaStateRef.current = null;
        setFindPanoramaTriesCounter(0);
        setIsSearching(false);
        setPanoramaFounded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originalPanoramaPlace, bestPanoramaPlace]);

    // ===== Mode change → zahoď preload (preload byl pro starou vzdálenost) =====

    useEffect(() => {
        discardPreload();
        consecutiveForwardJumpsRef.current = 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panoramaStepMode]);

    // ===== Cleanup při unmount =====

    useEffect(() => {
        return () => {
            debouncedPositionHandler.cancel?.();
            clearPreloadTimer();
            // Inkrementujeme generation tokeny — invaliduje case-flight async ops,
            // jejichž promise se může resolvnout až po unmountu (jinak by zapsali
            // stale state do refs unmounted komponenty).
            // eslint-disable-next-line react-hooks/exhaustive-deps
            preloadGenerationRef.current++;
            // eslint-disable-next-line react-hooks/exhaustive-deps
            swapGenerationRef.current++;
            destroyInstanceOnSide('A');
            destroyInstanceOnSide('B');
            preloadRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ===== Návrat na panorama tab (mobile) =====

    useEffect(() => {
        if (isVisible && !wasVisibleRef.current && bestPanoramaPlace) {
            const timeoutId = setTimeout(async () => {
                resetThrottleState();
                await discardPreload();
                // Návrat z background tabu je přerušení souvislé jízdy → reset čítače,
                // ať se po návratu nepreloaduje hned (konzistentní s backward/teleport).
                consecutiveForwardJumpsRef.current = 0;

                // Wrap v lock — když uživatel přepnul tab přesně v moment, kdy
                // jiná swap operace probíhá, počkáme než vstoupíme.
                await withSwapLock(async () => {
                    const last = lastPanoramaStateRef.current;
                    const init = initialPanoramaPositionRef.current;
                    if (last) {
                        await showPanoramaAt(
                            last.lon,
                            last.lat,
                            {
                                yaw: last.yaw,
                                pitch: last.pitch,
                                fov: last.fov,
                            },
                            isStepLimitReached,
                            false,
                        );
                    } else if (init) {
                        await showPanoramaAt(init.lon, init.lat, undefined, isStepLimitReached, false);
                    }
                });
            }, 100);

            return () => clearTimeout(timeoutId);
        }

        wasVisibleRef.current = isVisible;
    }, [isVisible, bestPanoramaPlace, showPanoramaAt, isStepLimitReached, discardPreload, withSwapLock]);

    // ===== Recreate panoramatu při změně shouldShowNativeNavigation =====

    useEffect(() => {
        const prev = prevShouldShowNavRef.current;
        const curr = shouldShowNativeNavigation;

        if (prev === curr) return;
        if (!bestPanoramaPlace) return;
        // Použijeme STATE (isSwapping) ne ref — když lock povolí, isSwapping flipne
        // false a effect se znovu spustí (state je v deps). Tím se nestane, že by
        // recreate "propadl" (Redux v fast módu, panorama stále native).
        if (isSwapping) return;

        const last = lastPanoramaStateRef.current;
        if (!last) return;

        // Update prev REF až když opravdu spouštíme recreate, ať se nám nestane,
        // že early-return nás zaseká v rozchozeném stavu (Redux říká fast, panorama
        // je ale stále s nativní navigací).
        prevShouldShowNavRef.current = curr;

        (async () => {
            await discardPreload();
            await withSwapLock(async () => {
                await swapToPanorama(
                    last.lon,
                    last.lat,
                    {
                        yaw: last.yaw,
                        pitch: last.pitch,
                        fov: last.fov,
                    },
                    {
                        showNavigation: curr,
                        isInitialLoad: false,
                    },
                );
            });
        })();
    }, [shouldShowNativeNavigation, bestPanoramaPlace, isSwapping, swapToPanorama, withSwapLock, discardPreload]);

    // ===== Pomocná tlačítka =====

    const handleGoBackToTheBeginning = useCallback(async () => {
        if (isSwappingRef.current) return;
        if (!initialPanoramaPositionRef.current) return;

        const { lon, lat } = initialPanoramaPositionRef.current;
        await discardPreload();
        consecutiveForwardJumpsRef.current = 0;
        await withSwapLock(async () => {
            dispatch(resetLastPanoramaPlace());
            lastPanoramaStateRef.current = null;
            await swapToPanorama(lon, lat, undefined, {
                showNavigation: shouldShowNativeNavigation,
                isInitialLoad: false,
            });
        });
    }, [dispatch, swapToPanorama, shouldShowNativeNavigation, withSwapLock, discardPreload]);

    const handleRefreshPanorama = useCallback(async () => {
        if (isSwappingRef.current) return;
        await discardPreload();
        consecutiveForwardJumpsRef.current = 0;
        await withSwapLock(async () => {
            resetThrottleState();
            const last = lastPanoramaStateRef.current;
            const init = initialPanoramaPositionRef.current;
            if (last) {
                await swapToPanorama(
                    last.lon,
                    last.lat,
                    {
                        yaw: last.yaw,
                        pitch: last.pitch,
                        fov: last.fov,
                    },
                    {
                        showNavigation: shouldShowNativeNavigation,
                        isInitialLoad: false,
                    },
                );
            } else if (init) {
                await swapToPanorama(init.lon, init.lat, undefined, {
                    showNavigation: shouldShowNativeNavigation,
                    isInitialLoad: false,
                });
            }
        });
    }, [swapToPanorama, shouldShowNativeNavigation, withSwapLock, discardPreload]);

    const handleGoToMunicipality = useCallback(async () => {
        if (isSwappingRef.current) return;
        if (isStepLimitReached || isFindingMunicipality) return;

        const currentPosition = lastPanoramaStateRef.current || initialPanoramaPositionRef.current;
        if (!currentPosition) return;

        // Generation token: pokud doběhne pomalý reverse-geocode + checkPanoramaExists
        // až po round change, jeho výsledek se zahodí (jinak by hráč v novém kole
        // dostal teleport do obce ze starého kola).
        const generation = swapGenerationRef.current;
        const isStale = () => generation !== swapGenerationRef.current;

        setIsFindingMunicipality(true);
        try {
            const municipality = await getNearestMunicipality(currentPosition.lat, currentPosition.lon);
            if (isStale()) return;
            if (!municipality) {
                console.warn('Nepodařilo se najít nejbližší obec');
                message.warning({
                    key: FAILURE_TOAST_KEY,
                    content: 'Nepodařilo se najít nejbližší obec.',
                    duration: 1.8,
                });
                return;
            }

            let target: { lat: number; lon: number } | null = null;
            // Sequential retry — vyzkoušíme různé radiusy postupně, dokud nějaký nenajde panorama.
            /* eslint-disable no-await-in-loop, no-restricted-syntax */
            for (const r of [1000, 3000]) {
                const exists = await checkPanoramaExists(municipality.lon, municipality.lat, r);
                if (isStale()) return;
                if (exists?.exists && exists.info) {
                    target = { lat: exists.info.lat, lon: exists.info.lon };
                    break;
                }
            }
            /* eslint-enable no-await-in-loop, no-restricted-syntax */

            if (!target) {
                message.warning({
                    key: FAILURE_TOAST_KEY,
                    content: 'V okolí obce není dostupné panorama.',
                    duration: 1.8,
                });
                return;
            }

            await discardPreload();
            consecutiveForwardJumpsRef.current = 0;
            if (isStale()) return;
            await withSwapLock(async () => {
                if (isStale()) return;
                const result = await swapToPanorama(target!.lon, target!.lat, undefined, {
                    showNavigation: shouldShowNativeNavigation,
                    isInitialLoad: false,
                });
                if (!result.success) return;
                if (isStale()) return;
                // Použij skutečnou pozici z pano.info (swap mohl snapnout na jinou
                // existující panoramu v rámci radius). lastPanoramaStateRef už byl
                // updatovaný v swapToPanorama, jen synchronizujeme Redux.
                const lat = result.actualLat ?? target!.lat;
                const lon = result.actualLon ?? target!.lon;
                dispatch(setLastPanoramaPlace({ lat, lon }));
                dispatch(incrementStepCount());
            });
        } catch (error) {
            if (isStale()) return;
            console.error('Chyba při hledání obce:', error);
        } finally {
            setIsFindingMunicipality(false);
        }
    }, [
        isStepLimitReached,
        isFindingMunicipality,
        swapToPanorama,
        shouldShowNativeNavigation,
        dispatch,
        checkPanoramaExists,
        withSwapLock,
        discardPreload,
    ]);

    // ===== Render =====

    const isPanoramaLoading = !originalPanoramaPlace || !isGameStarted || panoramaLoading || !bestPanoramaPlace;
    const showRefreshButton = bestPanoramaPlace && isGameStarted && !isPanoramaLoading;
    const showGoToMunicipalityButton =
        bestPanoramaPlace && isGameStarted && !isPanoramaLoading && !isStepLimitReached && !noMove;

    const showStepModeSwitcher = bestPanoramaPlace && isGameStarted && !isPanoramaLoading && !noMove;

    const showCustomNavigation =
        isFastMode && bestPanoramaPlace && isGameStarted && !isPanoramaLoading && !noMove && !isStepLimitReached;

    return (
        <Spin
            tip={!isGameStarted ? 'Čekám, až budou všichni hráči připraveni' : 'Načítám panorama, čekej prosím...'}
            spinning={isPanoramaLoading}
            size="large"
        >
            <div
                className="panorama-container"
                style={{ height: height - (isMobile ? 110 : 75), ...(!isMobile && { width }) }}
            >
                {!panoramaFounded ? (
                    <p>V okruhu 5 km od vašeho místa nebylo nalezeno žádné panorama.</p>
                ) : (
                    <PanoramaStack ref={panoramaStackRef}>
                        <PanoramaSlot $active={activeSide === 'A'}>
                            <div ref={containerARef} style={{ width: '100%', height: '100%' }} />
                        </PanoramaSlot>
                        <PanoramaSlot $active={activeSide === 'B'}>
                            <div ref={containerBRef} style={{ width: '100%', height: '100%' }} />
                        </PanoramaSlot>

                        {showStepModeSwitcher && (
                            <PanoramaStepModeSwitcher disabled={isStepLimitReached || isSwapping} />
                        )}

                        {showCustomNavigation && (
                            <CustomNavigation
                                onForward={() => handleFastJump('forward')}
                                onBackward={() => handleFastJump('backward')}
                                disabled={isStepLimitReached}
                                isLoading={isSwapping}
                            />
                        )}

                        <ActionGroup>
                            {showGoBackToTheBeginning && (
                                <Tooltip title="Vrátit se na výchozí pozici kola" placement="left">
                                    <ActionCard onClick={handleGoBackToTheBeginning}>
                                        <ActionIconBox $accent={ACCENT_RED} $glow={GLOW_RED}>
                                            <IconBack />
                                        </ActionIconBox>
                                        <ActionLabel>Zpět na výchozí místo</ActionLabel>
                                    </ActionCard>
                                </Tooltip>
                            )}
                            {showRefreshButton && (
                                <Tooltip title="Obnovit panorama (při zaseknutí)" placement="left">
                                    <ActionCard onClick={handleRefreshPanorama}>
                                        <ActionIconBox $accent={ACCENT_BLUE} $glow={GLOW_BLUE}>
                                            <IconRefresh />
                                        </ActionIconBox>
                                        <ActionLabel>Obnovit</ActionLabel>
                                    </ActionCard>
                                </Tooltip>
                            )}
                            {showGoToMunicipalityButton && (
                                <Tooltip title="Přesunout se do centra nejbližší obce" placement="left">
                                    <ActionCard onClick={handleGoToMunicipality} $disabled={isFindingMunicipality}>
                                        <ActionIconBox $accent={ACCENT_GREEN} $glow={GLOW_GREEN}>
                                            {isFindingMunicipality ? <InlineSpinner /> : <IconVillage />}
                                        </ActionIconBox>
                                        <ActionLabel>{isFindingMunicipality ? 'Hledám…' : 'Jít do obce'}</ActionLabel>
                                    </ActionCard>
                                </Tooltip>
                            )}
                        </ActionGroup>
                    </PanoramaStack>
                )}
            </div>
        </Spin>
    );
};

export default PanoramaNew;
